/**
 * ⭐⭐ **THE TALENT BRIEF — the report, reshaped for a PROMPT.**
 *
 * The Talent Discovery report is written *to* the user: second person, warm,
 * and built around a coined profile name ("The Idea Alchemist"). That is right
 * for the page it renders on and wrong as an input to the Purpose generation,
 * for three reasons found on 2026-08-13:
 *
 *  1. ⚠️⚠️ **ECHO.** A prompt that receives second-person prose and is then
 *     asked for a first-person purpose statement will paraphrase it. The worst
 *     vector is the coined name — `utils/profileName.ts` exists precisely
 *     because that H1 is treated as an identity label elsewhere, so a purpose
 *     coming back as "I exist to alchemize ideas…" would look like success and
 *     be failure.
 *  2. ⚠️⚠️ **OVER-WEIGHTING.** Ikigai has four pillars and the report speaks to
 *     exactly one. At ~2,000 characters against four lists of two or three
 *     short phrases, the report outweighs the person's own words — including
 *     the reflections that same prompt calls "the most personal signal".
 *     Making talents visible must not make them dominant.
 *  3. ⚠️ **SHAPE.** The purpose prompt does not need a report. It needs a few
 *     claims: what this combination makes them able to do, when it compounds,
 *     and what it does not cover.
 *
 * ⭐ The brief is produced by the SAME call that writes the report — it is one
 * JSON response with more keys — so it costs no additional AI action and no
 * additional Coin. See `ASSESSMENT_TALENT` in the ai-proxy prompts.
 */

/**
 * ⚠️⚠️ **A STORAGE KEY.** Rows are written to `user_assessment_results` under
 * this `result_type`; renaming it orphans every saved brief.
 * `user_assessment_results` already accepts an arbitrary `result_type` with a
 * jsonb payload, which is why this needed **no migration**.
 */
export const TALENT_BRIEF_RESULT_TYPE = 'talent_brief';

/** ⚠️ The scope/label column. One brief per attempt, so a fixed literal. */
export const TALENT_BRIEF_SCOPE = 'all';

export interface TalentBrief {
  /**
   * The combination in one line, THIRD person and WITHOUT the coined profile
   * name — e.g. "A creative lead supported by unusually strong self-reflection".
   */
  signature: string;
  /** How the strong talents reinforce one another, and under what conditions. */
  compounds: string;
  /** What the weakest talents practically cost, named plainly. */
  blindSpots: string;
  /** Kinds of work the mix pays off in. Short noun phrases, not job titles. */
  applications: string[];
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/**
 * ⚠️⚠️ **THE PROMPT ASKED FOR "UNDER 120 WORDS" AND GOT 161.** Measured live
 * 2026-08-13 on a real generation. A length rule stated only in a prompt is a
 * request, not a limit — and this one guards something real: the brief sits in
 * an Ikigai prompt beside four short pillar lists, so an over-long one restores
 * the exact over-weighting it was introduced to fix.
 *
 * Per-field rather than a single total: a global budget would truncate whichever
 * field happened to come last, so a verbose `signature` could silently delete
 * the blind spots. Each field gets its own allowance and is cut on its own.
 */
const FIELD_WORDS = 34;

/**
 * Cut to `max` words, preferring a SENTENCE boundary. A mid-sentence cut leaves
 * a dangling clause that a model will try to complete, which is worse than a
 * slightly shorter brief; the ellipsis is the fallback when no sentence ends
 * inside the budget.
 */
export function clampWords(text: string, max = FIELD_WORDS): string {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= max) return text;

  const cut = words.slice(0, max).join(' ');
  const lastStop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
  // ⚠️ Only honour a sentence break that keeps at least half the budget —
  // otherwise one early full stop throws most of the field away.
  if (lastStop > 0 && cut.slice(0, lastStop).split(/\s+/).length >= max / 2) {
    return cut.slice(0, lastStop + 1);
  }
  return `${cut.replace(/[,;:]$/, '')}…`;
}

/**
 * ⚠️ A brief is only worth storing if it says something. `signature` is the one
 * field the prompt cannot do without — the other three degrade gracefully — so
 * a payload missing it is rejected outright rather than stored half-empty,
 * where it would silently REPLACE the fuller raw report at prompt-build time.
 */
export function coerceTalentBrief(raw: unknown): TalentBrief | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const signature = str(r.signature);
  if (!signature) return null;
  return {
    // ⚠️ Clamped HERE, at the coercion, so the cap holds for a brief that was
    // stored before the limit existed as well as for a fresh one. Enforcing it
    // only at render would leave the over-long copy in the database.
    signature: clampWords(signature),
    compounds: clampWords(str(r.compounds)),
    blindSpots: clampWords(str(r.blindSpots)),
    applications: (Array.isArray(r.applications) ? r.applications : [])
      .map(str)
      .filter(Boolean)
      .slice(0, 5)
      // ⚠️ These are meant to be short noun phrases; a model that returns a
      // sentence per item is how the list quietly becomes a paragraph.
      .map(a => clampWords(a, 6)),
  };
}

/**
 * Render the brief for a prompt. Third person throughout, because the consumer
 * writes a first-person statement and mixing the two is how the model starts
 * quoting instead of reasoning.
 *
 * ⚠️ Every field is optional except `signature` (guaranteed by the coercion),
 * so each line is emitted only when it has content — a labelled empty line
 * reads to a model as "this is known to be nothing".
 */
export function renderTalentBrief(brief: TalentBrief): string {
  const lines = [`- Signature: ${brief.signature}`];
  if (brief.compounds) lines.push(`- How the talents compound: ${brief.compounds}`);
  if (brief.blindSpots) lines.push(`- Blind spots: ${brief.blindSpots}`);
  if (brief.applications.length) {
    lines.push(`- Pays off in: ${brief.applications.join(', ')}`);
  }
  return lines.join('\n');
}
